import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText from '@locale';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris';
import MenuGroup from './models/MenuGroup';
import Menu from './models/Menu';

class MenuGroupStore {
  @observable menuGroups = [];

  @observable isLoading = false;

  @observable currentMenuGroup = {};

  @observable assginMenus = [];

  @observable menus = [];

  @observable checkedKeys = [];

  @observable halfCheckedKeys = [];

  @observable defalutCheckedKeys = [];

  @observable currentAppId = '';

  @action resetCheckedKeys() {
    this.checkedKeys = this.defalutCheckedKeys;
  }

  @action resetLoading() {
    this.isLoading = false;
  }

  @action setMenuGroup(data) {
    this.menuGroups = data;
  }

  @action setAssignMenu(data) {
    this.assginMenus = data;
  }

  @action setMenu(data) {
    this.menus = data;
  }

  @action setCurrentAppId(appId) {
    this.currentAppId = appId;
  }

  @action setCheckedKeys(checkedKeys, halfCheckedKeys) {
    this.checkedKeys = checkedKeys;
    this.halfCheckedKeys = halfCheckedKeys;
  }

  @action.bound
  setDefaultCheckedKeys(data) {
    this.checkedKeys = [];
    this.defalutCheckedKeys = [];
    (data || []).forEach((item) => {
      if (item.isAssigned) {
        if (item.children.length === 0) {
          this.defalutCheckedKeys.push(item.id);
        } else {
          item.children.forEach((c) => {
            if (c.isAssigned) {
              this.defalutCheckedKeys.push(c.id);
            }
          });
        }
      }
    });
  }

  @action.bound
  setDefalutAppMenuGroupAssignList() {
    this.currentMenuGroup = this.menuGroups[0] || {};
    this.assginMenus = [];
  }

  @action.bound
  resetMenu() {
    this.assginMenus = [];
    this.menus = [];
    this.currentMenuGroup = {};
  }

  @action.bound
  async getAppMenuGroupList(appId) {
    this.isLoading = true;
    await axiosInstance
      .post(uris.getAppMenuGroupList(), null, {
        params: {
          appId
        }
      })
      .then((data) => {
        this.setMenuGroup((data.menuGroups || []).map(json => this.updateMenuGroupFromServer(json)));
        this.setDefalutAppMenuGroupAssignList();
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateMenuGroupFromServer(json) {
    let menuGroup = this.menuGroups.find(({id}) => id === json.id);
    if (!menuGroup) {
      menuGroup = new MenuGroup(this, json.id);
    }
    menuGroup.updateFromJson(json);
    return menuGroup;
  }

  @action.bound
  getAppMenuGroupAssignList(menuGroupId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getAppMenuGroupAssignList(), null, {
        params: {
          menuGroupId
        }
      })
      .then((data) => {
        this.setAssignMenu((data.menus || []).map(json => this.updateAssginMenuFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateAssginMenuFromServer(json) {
    let assginMenu = this.assginMenus.find(({id}) => id === json.id);
    if (!assginMenu) {
      assginMenu = new Menu(this, json);
    }
    assginMenu.updateChildren((json.children || []).map(item => new Menu(this, item)));
    return assginMenu;
  }

  @action.bound
  createAppMenuGroup(appId, menuGroupName) {
    axiosInstance
      .post(
        uris.createAppMenuGroup(),
        {
          appId,
          name: menuGroupName.trim()
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_ADD_FAIL')
          }
        }
      )
      .then(() => {
        message.success(localeText('MENU_GROUP.PRMTMSG_ADD_SUCCEED'));
        this.getAppMenuGroupList(appId);
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  changeCurrentMenuGroup(json) {
    this.currentMenuGroup = json;
  }

  @action.bound
  modifyAppMenuGroup(appId, newName) {
    this.isLoading = true;
    axiosInstance
      .post(
        uris.modifyAppMenuGroup(),
        {
          id: this.currentMenuGroup.id,
          appId,
          name: newName.trim()
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_MODIFY_FAIL')
          }
        }
      )
      .then(() => {
        this.resetLoading();
        this.updateMenuGroupNameFromServer(this.currentMenuGroup.id, newName.trim());
        message.success(localeText('MENU_GROUP.PRMTMSG_MODIFY_SUCCEED'));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateMenuGroupNameFromServer(menuGroupId, newName) {
    const menuGroup = this.menuGroups.find(({id}) => id === menuGroupId);
    menuGroup.name = newName;
  }

  @action.bound
  deleteAppMenuGroup() {
    this.isLoading = true;
    return axiosInstance
      .post(uris.deleteAppMenuGroup(), null, {
        params: {
          id: this.currentMenuGroup.id
        },
        showError: {
          showPopup: true,
          title: localeText('ROLE.TITLE_DELETE_FAIL')
        }
      })
      .then(() => {
        this.resetLoading();
        message.success(localeText('MENU_GROUP.PRMTMSG_DELETE_SUCCEED'));
        this.getAppMenuGroupList(this.currentAppId);
      })
      .catch((err) => {
        this.resetLoading();
        Promise.reject(err);
      });
  }

  @action.bound
  async getAppMenuGroupMenuList(menuGroupId) {
    this.isLoading = true;
    await axiosInstance
      .post(uris.getAppMenuGroupMenuList(), null, {
        params: {menuGroupId}
      })
      .then((data) => {
        this.setMenu((data.menus || []).map(json => this.updateMenuFromServer(json)));
        this.setDefaultCheckedKeys(data.menus);
        this.resetCheckedKeys();
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateMenuFromServer(json) {
    let menu = this.menus.find(({id}) => id === json.id);
    if (!menu) {
      menu = new Menu(this, json);
    }
    menu.updateChildren((json.children || []).map(item => new Menu(this, item)));
    return menu;
  }

  @action.bound
  assignMenuGroupMenu() {
    this.isLoading = true;
    axiosInstance
      .post(uris.assignMenuGroupMenu(), {
        menuGroupId: this.currentMenuGroup.id,
        menuIds: this.checkedKeys.concat(this.halfCheckedKeys),
        appId: this.currentAppId
      })
      .then(() => {
        this.getAppMenuGroupAssignList(this.currentMenuGroup.id);
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  checkNameExist(name, id, cb) {
    axiosInstance
      .post(uris.checkNameExist(), null, {
        params: {type: 'menuGroup', name, id},
        showError: {isShow: false}
      })
      .then((data) => {
        if (!data) {
          cb(false);
        } else {
          cb();
        }
      })
      .catch((error) => {
        cb(error);
      });
  }
}

export default new MenuGroupStore();
